import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { NgModule, Injectable } from '@angular/core';
import { CoreFormsModule } from '@yourcause/common/core-forms';
import { CurrencyModule } from '@yourcause/common/currency';
import * as i1 from '@yourcause/common/i18n';
import { I18nService } from '@yourcause/common/i18n';
import { Validators, UntypedFormControl } from '@angular/forms';
import { isRegExp, uniq, isBoolean, get, trim } from 'lodash';

class FormControlValidationModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FormControlValidationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: FormControlValidationModule, imports: [CommonModule,
            CoreFormsModule,
            CurrencyModule] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FormControlValidationModule, imports: [CommonModule,
            CoreFormsModule,
            CurrencyModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FormControlValidationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        CoreFormsModule,
                        CurrencyModule
                    ]
                }]
        }] });

var RegexUI;
(function (RegexUI) {
    let RegexFormattingType;
    (function (RegexFormattingType) {
        RegexFormattingType[RegexFormattingType["NONE"] = 0] = "NONE";
        RegexFormattingType[RegexFormattingType["EMAIL"] = 1] = "EMAIL";
        RegexFormattingType[RegexFormattingType["TIME_24_HOUR"] = 2] = "TIME_24_HOUR";
        RegexFormattingType[RegexFormattingType["TIME_12_HOUR"] = 3] = "TIME_12_HOUR";
        RegexFormattingType[RegexFormattingType["EIN"] = 4] = "EIN";
        RegexFormattingType[RegexFormattingType["URL_OPT_HTTP"] = 5] = "URL_OPT_HTTP";
        RegexFormattingType[RegexFormattingType["URL_REQ_HTTP"] = 6] = "URL_REQ_HTTP";
    })(RegexFormattingType = RegexUI.RegexFormattingType || (RegexUI.RegexFormattingType = {}));
    RegexUI.RegexFormattingTypeEnumMap = {
        [RegexUI.RegexFormattingType.NONE]: 'common:textNone',
        [RegexUI.RegexFormattingType.EIN]: 'common:textEIN',
        [RegexUI.RegexFormattingType.EMAIL]: 'common:textEmail',
        [RegexUI.RegexFormattingType.TIME_12_HOUR]: 'common:textTimeTwelveHour',
        [RegexUI.RegexFormattingType.TIME_24_HOUR]: 'common:textTimeTwentyFourHour',
        [RegexUI.RegexFormattingType.URL_OPT_HTTP]: 'common:textURL',
        [RegexUI.RegexFormattingType.URL_REQ_HTTP]: 'common:textURLHttpRequired'
    };
    // https://emailregex.com/ > Javascript
    RegexUI.emailRegEx = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    RegexUI.RegexFormattingValues = {
        EMAIL: RegexUI.emailRegEx,
        URL_REQ_HTTP: /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/,
        URL_OPT_HTTP: /[(http(s)?):\/\/(www\.)?a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/,
        TIME_12_HOUR: /^((1[0-2]|0?[1-9]):([0-5][0-9]) ?(AM|PM|am|pm))$/,
        TIME_24_HOUR: /^((0[0-9]|1[0-9]|2[0-3])|[0-23]):[0-5][0-9]$/,
        EIN: /^[0-9]\d(-?)\d{7}$/,
        ASCII: /^[\x00-\x7F]*$/
    };
})(RegexUI || (RegexUI = {}));

function RegexValidator(pattern, errorMessageObj) {
    return (control) => {
        const val = control.value;
        const defaultErrorMessageObj = {
            doesNotMatchFormat: {
                i18nKey: 'common:textDoesNotMatchExpectedFormat',
                defaultValue: 'Does not match expected format'
            }
        };
        let valid = false;
        if (val instanceof Array) {
            valid = val.every((input) => {
                return !input || pattern.test(input);
            });
        }
        else {
            valid = !val || pattern.test(val);
        }
        const error = errorMessageObj ?? defaultErrorMessageObj;
        return valid ? null : error;
    };
}

class InputRegexService {
    constructor(i18n) {
        this.i18n = i18n;
        this.mustBeValidEmailMessage = 'This must be a valid email address';
        this.mustBeValidEin = 'This must be a valid EIN';
        this.mustBeValidUrlReqHttp = 'This must be a valid URL with \'http\' or \'https\'';
        this.mustBeValidUrl = 'This must be a valid URL';
        this.mustBe24HourTime = 'This must be a valid 24 hour time';
        this.mustBe12HourTime = 'This must be a valid 12 hour time with AM or PM';
        this.mustAddUpTo100 = 'Must add up to 100%';
        this.emailErrorObj = {
            mustBeAValidEmail: {
                i18nKey: 'common:textMustBeAValidEmail',
                defaultValue: this.mustBeValidEmailMessage
            }
        };
        this.einErrorObj = {
            mustBeAValidEIN: {
                i18nKey: 'common:textMustBeAValidEIN',
                defaultValue: this.mustBeValidEin
            }
        };
        this.urlHttpReqErrorObj = {
            mustBeValidURLHttpReq: {
                i18nKey: 'common:textMustBeValidURLWithHttp',
                defaultValue: this.mustBeValidUrlReqHttp
            }
        };
        this.urlErrorObj = {
            mustBeAValidURL: {
                i18nKey: 'common:textMustBeAValidURL',
                defaultValue: this.mustBeValidUrl
            }
        };
        this.time24HourErrorObj = {
            mustBeAValidTime24: {
                i18nKey: 'common:textMustBeAValidTime24',
                defaultValue: this.mustBe24HourTime
            }
        };
        this.time12HourErrorObj = {
            mustBeAValid12HourTime: {
                i18nKey: 'common:textMustBeAValidTime12',
                defaultValue: this.mustBe12HourTime
            }
        };
    }
    /**
     * @param formatting: Enum for preset formats on text fields
     * @returns Formatting details will include the name of the option (eg. Email or URL), the regex for that format, a validator for formControls with a preset error message, and components of the validator message
     */
    getSelectedFormattingDetails(formatting, errorMessageOverride) {
        let result;
        const errorObjOveride = errorMessageOverride ? {
            incorrectFormatCustomMsg: {
                i18nKey: '',
                defaultValue: '',
                errorMessage: errorMessageOverride
            }
        } : null;
        switch (formatting) {
            case (RegexUI.RegexFormattingType.EMAIL):
                result = {
                    name: this.i18n.translate('common:textEmail', {}, 'Email'),
                    regex: RegexUI.RegexFormattingValues.EMAIL,
                    validator: RegexValidator(RegexUI.RegexFormattingValues.EMAIL, errorObjOveride ?? this.emailErrorObj),
                    errorObj: this.emailErrorObj,
                    errorKey: 'mustBeAValidEmail'
                };
                break;
            case (RegexUI.RegexFormattingType.EIN):
                result = {
                    name: this.i18n.translate('common:textEIN', {}, 'EIN'),
                    regex: RegexUI.RegexFormattingValues.EIN,
                    validator: RegexValidator(RegexUI.RegexFormattingValues.EIN, errorObjOveride ?? this.einErrorObj),
                    errorObj: this.einErrorObj,
                    errorKey: 'mustBeAValidEIN'
                };
                break;
            case (RegexUI.RegexFormattingType.URL_OPT_HTTP):
                result = {
                    name: this.i18n.translate('common:textURL', {}, 'URL'),
                    regex: RegexUI.RegexFormattingValues.URL_OPT_HTTP,
                    validator: RegexValidator(RegexUI.RegexFormattingValues.URL_OPT_HTTP, errorObjOveride ?? this.urlErrorObj),
                    errorObj: this.urlErrorObj,
                    errorKey: 'mustBeAValidURL'
                };
                break;
            case (RegexUI.RegexFormattingType.URL_REQ_HTTP):
                result = {
                    name: this.i18n.translate('common:textURLHttpRequired', {}, 'URL (http/https required)'),
                    regex: RegexUI.RegexFormattingValues.URL_REQ_HTTP,
                    validator: RegexValidator(RegexUI.RegexFormattingValues.URL_REQ_HTTP, errorObjOveride ?? this.urlHttpReqErrorObj),
                    errorObj: this.urlHttpReqErrorObj,
                    errorKey: 'mustBeValidURLHttpReq'
                };
                break;
            case (RegexUI.RegexFormattingType.TIME_12_HOUR):
                result = {
                    name: this.i18n.translate('common:textTimeTwelveHour', {}, 'Time (12 hour)'),
                    regex: RegexUI.RegexFormattingValues.TIME_12_HOUR,
                    validator: RegexValidator(RegexUI.RegexFormattingValues.TIME_12_HOUR, errorObjOveride ?? this.time12HourErrorObj),
                    errorObj: this.time12HourErrorObj,
                    errorKey: 'mustBeAValid12HourTime'
                };
                break;
            default:
            case (RegexUI.RegexFormattingType.TIME_24_HOUR):
                result = {
                    name: this.i18n.translate('common:textTimeTwentyFourHour', {}, 'Time (24 hour)'),
                    regex: RegexUI.RegexFormattingValues.TIME_24_HOUR,
                    validator: RegexValidator(RegexUI.RegexFormattingValues.TIME_24_HOUR, errorObjOveride ?? this.time24HourErrorObj),
                    errorObj: this.time24HourErrorObj,
                    errorKey: 'mustBeAValidTime24'
                };
                break;
        }
        return result;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: InputRegexService, deps: [{ token: i1.I18nService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: InputRegexService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: InputRegexService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.I18nService }] });

function confirmationValidator(leftControlName, rightControlName) {
    return (control) => {
        const left = control.get(leftControlName);
        const right = control.get(rightControlName);
        let returnVal = null;
        if (left && right) {
            returnVal = left.value !== right.value ? {
                [rightControlName]: {
                    errorMessage: `Values must match`
                }
            } : null;
        }
        if (returnVal) {
            right.setErrors({
                confirmation: {
                    errorMessage: `Value must match ${leftControlName}`
                }
            });
        }
        return returnVal;
    };
}

const CSV_COLUMN_REGEX = /(<)|(>)|(&)/;
const FIRST_CHARACTER_REGEX = /^[=@{}<]/;
function CsvColumnValidator(fieldName) {
    return (group) => {
        const val = group.get(fieldName).value;
        const error = {
            [fieldName]: {
                invalidCharacters: {
                    i18nKey: 'common:textCsvColumnCharactersNotAllowed',
                    defaultValue: 'Cannot Contain: &<> or begin with one of =@{}<'
                }
            }
        };
        const csvColumnMatch = CSV_COLUMN_REGEX.test(val);
        const firstCharacterMatch = FIRST_CHARACTER_REGEX.test(val);
        if (!csvColumnMatch && !firstCharacterMatch) {
            return null;
        }
        else {
            return error;
        }
    };
}

function DuplicateCheckValidator(fieldName, siblingFieldName, validators = [Validators.required]) {
    return (group) => {
        let hasDuplicates = false;
        const control = group.get(fieldName);
        const siblingControl = group.get(siblingFieldName);
        if (control && control.value && siblingControl && siblingControl.value) {
            if (Array.isArray(control.value)) {
                control.value.forEach((val) => {
                    if (!hasDuplicates) {
                        const found = siblingControl.value.find((searchItem) => searchItem === val);
                        hasDuplicates = !!found;
                    }
                });
            }
            else if (typeof control.value === 'object') {
                for (const key in control.value) {
                    if (siblingControl.value.hasOwnProperty(key) && !hasDuplicates) {
                        hasDuplicates = control.value[key] === siblingControl.value[key];
                    }
                }
            }
            else {
                hasDuplicates = control.value === siblingControl.value;
            }
        }
        return hasDuplicates ? {
            [fieldName]: {
                duplicatesFound: {
                    i18nKey: 'common:textDuplicateValueFound',
                    defaultValue: 'Duplicates found.  Please remove to continue.'
                }
            }
        } : null;
    };
}

// Custom validator to replace EmailValidator from email.validator.ts
// and angular Validators.email. Includes angular validation rules &&
// checks for ext with min 2 chars.
// Can be used to validate one val or array (cc/bcc emails)
function EmailExtensionValidator(control) {
    const EMAIL_REGEX = RegexUI.emailRegEx;
    const val = control.value;
    let invalid = false;
    if (val && val instanceof Array) {
        val.forEach((email) => {
            if (!email.match(EMAIL_REGEX)) {
                invalid = true;
            }
        });
    }
    else {
        invalid = val && !val.match(EMAIL_REGEX);
    }
    return invalid ?
        {
            invalidEmail: {
                i18nKey: 'common:textPleaseEnterValidEmail',
                defaultValue: 'Please enter a valid email'
            }
        } :
        null;
}

function EmailValidator() {
    return (control) => {
        const val = control.value;
        let invalid = false;
        if (val instanceof Array) {
            val.forEach((email) => {
                if (!!Validators.email(new UntypedFormControl(email))) {
                    invalid = true;
                }
            });
        }
        else {
            invalid = !!Validators.email(new UntypedFormControl(val));
        }
        return invalid ?
            {
                invalidEmail: {
                    i18nKey: 'common:textPleaseEnterValidEmail',
                    defaultValue: 'Please enter a valid email'
                }
            } :
            null;
    };
}

function ExactArrayLengthValidator(length) {
    return (control) => control.value.length === length ? null : {
        exactArrayLength: {
            length: 1
        }
    };
}

function MaskValidator(inputMask, customErrorMessage, defaultErrorMessage) {
    return (control) => {
        let invalid = false;
        const value = control.value ?? '';
        if (value) {
            for (let i = 0; i < inputMask.length; i++) {
                const char = value[i];
                const charPart = inputMask[i];
                if (!(isRegExp(charPart) && charPart.test(char) || charPart === char)) {
                    invalid = true;
                }
            }
        }
        if (invalid) {
            return {
                maskInvalid: {
                    i18nKey: '',
                    defaultValue: '',
                    errorMessage: customErrorMessage || defaultErrorMessage
                }
            };
        }
        return null;
    };
}
;

function MinMaxArrayValidator(attr, minMaxValue, customErrorMessage, defaultErrorMessage) {
    return (control) => {
        const errorMessage = customErrorMessage || defaultErrorMessage;
        const arrayValue = (control.value ?? []);
        let returnVal = null;
        switch (attr) {
            case 'min':
                if (arrayValue?.length < minMaxValue) {
                    returnVal = {
                        minArrayError: {
                            i18nKey: '',
                            defaultValue: '',
                            errorMessage
                        }
                    };
                }
                break;
            case 'max':
                if (arrayValue?.length > minMaxValue) {
                    returnVal = {
                        maxArrayError: {
                            i18nKey: '',
                            defaultValue: '',
                            errorMessage
                        }
                    };
                }
                break;
        }
        return returnVal;
    };
}

function MinMaxWordsValidator(attr, minMaxValue, customErrorMessage, defaultErrorMessage) {
    return (control) => {
        const errorMessage = customErrorMessage || defaultErrorMessage;
        const value = control.value?.trim().split(/\s+/) ?? '';
        let returnVal = null;
        switch (attr) {
            case 'minWords':
                const passMin = (value !== '') &&
                    value.length >= minMaxValue;
                if (!passMin) {
                    returnVal = {
                        minWordsError: {
                            i18nKey: '',
                            defaultValue: '',
                            errorMessage
                        }
                    };
                }
                break;
            case 'maxWords':
                const passMax = value.length <= minMaxValue;
                if (!passMax) {
                    returnVal = {
                        maxWordsError: {
                            i18nKey: '',
                            defaultValue: '',
                            errorMessage
                        }
                    };
                }
                break;
        }
        return returnVal;
    };
}

function MinMaxValidator(attr, minMaxValue, customErrorMessage, defaultErrorMessage, isCurrencyField) {
    return (control) => {
        const errorMessage = customErrorMessage || defaultErrorMessage;
        let value = control.value;
        if (isCurrencyField) {
            value = value.amountForControl || 0;
        }
        let returnVal = null;
        switch (attr) {
            case 'min':
                if ((value || value === 0) &&
                    value < minMaxValue) {
                    returnVal = {
                        minCustom: {
                            i18nKey: '',
                            defaultValue: '',
                            errorMessage
                        }
                    };
                }
                break;
            case 'max':
                if ((value || value === 0) &&
                    value > minMaxValue) {
                    returnVal = {
                        maxCustom: {
                            i18nKey: '',
                            defaultValue: '',
                            errorMessage
                        }
                    };
                }
                break;
            case 'minLength':
                if (value &&
                    value.trim().length < minMaxValue) {
                    returnVal = {
                        minLengthCustom: {
                            i18nKey: '',
                            defaultValue: '',
                            errorMessage
                        }
                    };
                }
                break;
            case 'maxLength':
                if (value &&
                    value.trim().length > minMaxValue) {
                    returnVal = {
                        maxLengthCustom: {
                            i18nKey: '',
                            defaultValue: '',
                            errorMessage
                        }
                    };
                }
                break;
        }
        return returnVal;
    };
}

function PatternValidator(pattern, customErrorMessage, defaultErrorMessage) {
    return (control) => {
        const value = control.value;
        if (value) {
            const regex = new RegExp(`^${pattern}$`);
            const pass = regex.test(value);
            if (!pass) {
                return {
                    patternInvalid: {
                        i18nKey: '',
                        defaultValue: '',
                        errorMessage: customErrorMessage || defaultErrorMessage
                    }
                };
            }
        }
        return null;
    };
}
;

const REFLECTED_DATA_REGEX = /(;)|(:)|(<)|(>)|({)|(})|(\[)|(\])|(,)|(\.)|(!)|(@)|(\()|(\))|(\\)|(\/)|(=)/;
const TEXT_FRIENDLY_REGEX = /(;)|(:)|(<)|(>)|({)|(})|(\[)|(\])|(,)|(!)|(@)|(\()|(\))|(\\)|(\/)|(=)|['"]/g;
function ReflectedDataValidator(fieldName, regexPattern = REFLECTED_DATA_REGEX) {
    const charactersToExclude = regexPattern.source
        .replace(/\//g, '') // Remove forward slash
        .replace(/\(/g, '') // Remove opening parenthesis
        .replace(/\)/g, '') // Remove closing parenthesis
        .replace(/\|/g, '') // Remove pipe
        .replace(/(?<!\\)\\/g, ''); // Remove backslash not followed by another backslash
    return (group) => {
        const val = group.get(fieldName).value;
        const error = {
            [fieldName]: {
                invalidCharacters: {
                    i18nKey: 'common:textCharactersToExclude',
                    context: {
                        charactersToExclude
                    },
                    defaultValue: 'Cannot Contain: __charactersToExclude__'
                }
            }
        };
        const valid = !regexPattern.test(val);
        return valid ? null : error;
    };
}
function TextFriendlySpecialCharCleaner(stringData) {
    let cleanedVal;
    if (stringData) {
        // replace all invalid chars with an empty string
        cleanedVal = stringData.replace(TEXT_FRIENDLY_REGEX, '');
    }
    return cleanedVal;
}

function RequiredArrayValidator(customErrorMessage, defaultErrorMessage) {
    return (control) => {
        const value = control.value ?? [];
        const pass = value?.length > 0 &&
            value?.some((input) => {
                if (typeof input === 'string') {
                    return !!input.trim();
                }
                return !!input;
            });
        if (!pass) {
            return {
                requiredArray: {
                    i18nKey: '',
                    defaultValue: '',
                    errorMessage: customErrorMessage || defaultErrorMessage
                }
            };
        }
        return null;
    };
}
;

function RequiredCheckboxValidator(customErrorMessage, defaultErrorMessage) {
    return (control) => {
        const value = control.value;
        if (!value) {
            return {
                required: {
                    i18nKey: '',
                    defaultValue: '',
                    errorMessage: customErrorMessage || defaultErrorMessage
                }
            };
        }
        return null;
    };
}
;

function RequiredCustomValidator(customErrorMessage, defaultErrorMessage) {
    return (control) => {
        const errorMessage = customErrorMessage || defaultErrorMessage;
        let value = control.value;
        if (typeof value === 'string') {
            value = value.trim();
        }
        let returnVal = null;
        if (!value && value !== 0) {
            returnVal = {
                requiredCustom: {
                    i18nKey: '',
                    defaultValue: '',
                    errorMessage
                }
            };
        }
        return returnVal;
    };
}

const EVALUATE_CHANGE = Symbol();
function SiblingValueValidator(fieldName, siblingFieldName, siblingValue, validators = [Validators.required]) {
    return (group) => {
        let returnVal = null;
        const control = group.get(fieldName);
        const currentSiblingValue = group.get(siblingFieldName).value;
        if (siblingValue === currentSiblingValue) {
            const validation = Validators.compose(validators)(control);
            returnVal = validation ? {
                [fieldName]: validation
            } : null;
        }
        return returnVal;
    };
}

// Sourced from https://urlregex.com/
const URL_REGEX = /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[\-;:&=\+\$,\w]+@)?[A-Za-z0-9\.\-]+|(?:www\.|[\-;:&=\+\$,\w]+@)[A-Za-z0-9\.\-]+)((?:\/[\+~%\/\.\w\-_]*)?\??(?:[\-\+=&;%@\.\w_]*)#?(?:[\.\!\/\\\w]*))?)/;

const URLValidator = () => (control) => {
    if (!!control.value && typeof control.value === 'string') {
        return control.value.trim().match(URL_REGEX) !== null ?
            null : {
            invalidURL: {
                i18nKey: 'common:textPleaseEnterValidURL',
                defaultValue: 'Please enter a valid URL'
            }
        };
    }
    return null;
};

const topLevelValidatorKey = Symbol('TOP_LEVEL');
const indStorage = new WeakMap();
const transformStorage = new WeakMap();
function Transform(transformation) {
    return (target, prop) => {
        const existing = transformStorage.get(target.constructor) || {};
        existing[prop] = transformation;
        transformStorage.set(target.constructor, existing);
    };
}
function applyTransformations(objects, target) {
    const transformations = getMap(target, transformStorage);
    return objects.map(object => {
        Object.keys(transformations)
            .map(k => k)
            .forEach(key => {
            object[key] = transformations[key](object[key], object);
        });
        return object;
    });
}
function getOrganizedErrors(errors, i18n, arrayHelper) {
    return errors.reduce((acc, errorMap, index) => [
        ...acc,
        ...Object.keys(errorMap || {})
            .map(k => k)
            .reduce((_acc, key) => {
            return [
                ..._acc,
                ...errorMap[key].map((error) => {
                    let message = i18n.translate(error.i18nKey, error.context, error.defaultValue);
                    if (!!error.enumMapping) {
                        const enumInfo = arrayHelper.getEnumMappingForDisplay(error.enumMapping, false);
                        message = `${message} ${enumInfo}`;
                    }
                    return {
                        row: index + 2,
                        column: key,
                        message,
                        get validator() {
                            return error.validator;
                        }
                    };
                })
            ];
        }, [])
    ], []);
}
function createTopLevelValidator(validator) {
    const Decorator = function (arg, extras) {
        const assembled = validator(arg, extras);
        assembled.original = Decorator;
        assembled.extras = extras;
        return (target) => {
            setupStorage(target, topLevelValidatorKey, assembled);
        };
    };
    return Decorator;
}
/**
 *
 * @param validator This is the validation function
 * @param validatorDefinition This is the data used for generating helpful info (e.g. showing the rules for each column)
 * @returns A Decorator that can be applied in order to build a validator for imports
 */
function createValidator(validator, validatorDefinition) {
    const Decorator = function (arg, extras) {
        const assembled = validator(arg, extras);
        assembled.original = Decorator;
        assembled.arg = arg;
        // applies either the default or passed in ruleText
        const assembledWithDynamicParams = applyDefaultExtras(assembled, extras, validatorDefinition);
        return (target, attr) => {
            setupStorage(target.constructor, attr, assembledWithDynamicParams);
        };
    };
    return Decorator;
}
/**
 *
 * @param validator This is the validator we are setting up
 * @param extras This is passed into the decorator where it's applied and will override the defaults
 * @param validatorDefinition This is where we are getting the default extras (ruleText)from
 * @returns The validator with the metadata for showing the rules for import
 */
function applyDefaultExtras(validator, extras, validatorDefinition) {
    let validatorExtras = {};
    // we might have one, both, or neither of these args so we have to account for that
    if (extras || validatorDefinition) {
        // create empty object for assignment
        // combine all keys from extras and default
        const allKeys = Object.keys({
            ...extras,
            ...validatorDefinition
        });
        // iterate over the unique keys
        uniq(allKeys).forEach((key) => {
            if (extras && key in extras) {
                // if it's been passed in dynamically, we use it
                validatorExtras = {
                    ...validatorExtras,
                    [key]: extras[key]
                };
            }
            else if (validatorDefinition && key in validatorDefinition) {
                validatorExtras = {
                    ...validatorExtras,
                    [key]: validatorDefinition[key]
                };
            }
        });
    }
    validator.extras = validatorExtras;
    return validator;
}
async function validateMultiple(ValidationClass, rows, injector, externalContext, returnMapByRecordIdentifier = false, recordIdentifierAttribute) {
    let recordLevelErrorsMap = {};
    const context = {};
    const validatorMap = getValidatorMap(ValidationClass);
    const validatorKeys = Object.keys(validatorMap)
        .map(k => k);
    const topLevelValidators = validatorMap[topLevelValidatorKey];
    let topLevelErrors = [];
    if (topLevelValidators?.length > 0) {
        await Promise.all(topLevelValidators.map(async (validator) => {
            let result = await validator(rows, {
                externalContext,
                context,
                injector
            }) || [];
            result = adaptValidatorResult(result, validator);
            topLevelErrors = [
                ...topLevelErrors,
                ...result.map(r => {
                    return {
                        ...r,
                        validator: validator.original
                    };
                })
            ];
        }));
    }
    const recordLevelErrors = await Promise.all(rows.map(async (row) => {
        let acc = {};
        await Promise.all(validatorKeys.map(async (attr) => {
            const validators = validatorMap[attr];
            let _acc = [];
            await Promise.all(validators.map(async (validator) => {
                try {
                    let result = [];
                    result = await validator(row[attr], {
                        attr,
                        ent: row,
                        group: rows,
                        injector,
                        context,
                        externalContext
                    }) || [];
                    result = adaptValidatorResult(result, validator);
                    _acc = [
                        ..._acc,
                        ...result.map(r => ({
                            ...r,
                            validator: validator.original,
                            target: row,
                            attr
                        }))
                    ];
                }
                catch (e) {
                    console.error('Validator exception', e);
                }
            }));
            acc = {
                ...acc,
                [attr]: _acc
            };
        }));
        if (returnMapByRecordIdentifier) {
            recordLevelErrorsMap = {
                ...recordLevelErrorsMap,
                [row[recordIdentifierAttribute]]: acc
            };
        }
        return acc;
    }));
    return {
        topLevelErrors,
        recordLevelErrors,
        recordLevelErrorsMap
    };
}
function adaptValidatorResult(result, validator) {
    if (!(result instanceof Array)) {
        result = [result];
    }
    if (result?.length && validator.extras?.message) {
        result = [validator.extras?.message];
    }
    return result;
}
function getKeys(ValidatorClass) {
    const fullMap = getValidatorMap(ValidatorClass);
    return Object.keys(fullMap);
}
// walks up the "prototype tree" of the validator class
// and gets every validator
function getValidatorMap(ValidatorClass) {
    // start with fetching the validators on the ValidatorClass argument
    const fullMap = getMap(ValidatorClass, indStorage);
    // return all of the validators
    return fullMap;
}
function getMap(ValidatorClass, storage) {
    let CurrentClass = ValidatorClass;
    let currentMap = storage.get(ValidatorClass) || {};
    let fullMap = {};
    // while we have validators to add to the overall map
    while (CurrentClass) {
        currentMap = storage.get(CurrentClass) || {};
        // spread the new ones on top of the old ones
        fullMap = {
            ...currentMap,
            ...fullMap
        };
        // prep the next iteration of the loop with the parent class of the current class
        CurrentClass = Object.getPrototypeOf(CurrentClass);
        // and the validators from the parent
        // the loop will end if the parent has no validators
    }
    return fullMap;
}
function setupStorage(target, attr, val) {
    let existing = indStorage.get(target);
    if (!existing) {
        existing = {};
    }
    existing = {
        ...existing,
        [attr]: [
            ...(existing[attr] || []),
            val
        ]
    };
    indStorage.set(target, existing);
    return existing;
}
const Optional = createValidator(() => (val) => {
    return [];
}, {
    ruleText: {
        i18nKey: 'common:textOptional',
        defaultValue: 'Optional'
    }
});
const Required = createValidator(() => (val) => {
    if (valueIsEmpty(val)) {
        return {
            i18nKey: 'common:textRequired',
            defaultValue: 'Required'
        };
    }
    return [];
}, {
    ruleText: {
        i18nKey: 'common:textRequired',
        defaultValue: 'Required'
    }
});
const RequiredFormCheckbox = createValidator(() => (val) => {
    if (!val) {
        return {
            i18nKey: 'common:textRequired',
            defaultValue: 'Required'
        };
    }
    return [];
});
// required if another row has a value for this column
const RequiredIfOtherHasValue = createValidator(() => (value, context) => {
    const atLeastOneHasValue = context.group.some((val) => !valueIsEmpty(val[context.attr]));
    if (atLeastOneHasValue && valueIsEmpty(value)) {
        return {
            i18nKey: 'common:textRequired',
            defaultValue: 'Required'
        };
    }
    return [];
}, {
    ruleText: {
        i18nKey: 'common:textRequiredIfAnyRowHasValue',
        defaultValue: 'Required if any row in the import file has a value for this column'
    }
});
const handleMinMaxValidation = (val, config) => {
    if (config) {
        if ('min' in config) {
            if (val < config.min) {
                return {
                    i18nKey: 'common:textMustBeAtLeastMin',
                    defaultValue: 'Must be at least __min__',
                    context: {
                        min: '' + config.min
                    }
                };
            }
        }
        if ('max' in config) {
            if (val > config.max) {
                return {
                    i18nKey: 'common:textCanOnlyBeMax',
                    defaultValue: 'Can only be at most __max__',
                    context: {
                        max: '' + config.max
                    }
                };
            }
        }
    }
    return [];
};
const IsNumberValidator = (config) => (val) => {
    if (valueIsEmpty(val)) {
        return [];
    }
    if (isNaN(val) || isBoolean(val)) {
        return {
            i18nKey: 'common:textMustBeANumber',
            defaultValue: 'Must be a number',
        };
    }
    return handleMinMaxValidation(val, config);
};
const IsIntegerValidator = (config) => (val) => {
    if (valueIsEmpty(val)) {
        return [];
    }
    if (isNaN(val) || isBoolean(val)) {
        return {
            i18nKey: 'common:textMustBeANumber',
            defaultValue: 'Must be a number',
        };
    }
    else if (!Number.isInteger(+val)) {
        return {
            i18nKey: 'common:textMustBeAnInteger',
            defaultValue: 'Must be an integer',
        };
    }
    return handleMinMaxValidation(val, config);
};
function getNumberRuleText(config) {
    const hasMin = config?.min || config?.min === 0;
    const hasMax = config?.max || config?.max === 0;
    if (hasMin && hasMax) {
        return {
            i18nKey: 'common:textMustBeANumberMinMax',
            context: {
                min: config.min,
                max: config.max
            },
            defaultValue: 'Must be a number. Minimum of __min__ and maximum of __max__'
        };
    }
    else if (hasMin) {
        return {
            i18nKey: 'common:textMustBeANumberMin',
            context: {
                min: config.min
            },
            defaultValue: 'Must be a number. Minimum of __min__'
        };
    }
    else if (hasMax) {
        return {
            i18nKey: 'common:textMustBeANumberMax',
            context: {
                max: config.max
            },
            defaultValue: 'Must be a number. Maximum of __max__'
        };
    }
    else {
        return {
            i18nKey: 'common:textMustBeANumber',
            defaultValue: 'Must be a number'
        };
    }
}
function getIntegerRuleText(config) {
    const hasMin = config?.min || config?.min === 0;
    const hasMax = config?.max || config?.max === 0;
    if (hasMin && hasMax) {
        return {
            i18nKey: 'common:textMustBeAnIntegerMinMax',
            context: {
                min: config.min,
                max: config.max
            },
            defaultValue: 'Must be an integer. Minimum of __min__ and maximum of __max__'
        };
    }
    else if (hasMin) {
        return {
            i18nKey: 'common:textMustBeAnIntegerMin',
            context: {
                min: config.min
            },
            defaultValue: 'Must be an integer. Minimum of __min__'
        };
    }
    else if (hasMax) {
        return {
            i18nKey: 'common:textMustBeAnInegerMax',
            context: {
                max: config.max
            },
            defaultValue: 'Must be an integer. Maximum of __max__'
        };
    }
    else {
        return {
            i18nKey: 'common:textMustBeAnInteger',
            defaultValue: 'Must be an integer'
        };
    }
}
const IsNumber = createValidator(IsNumberValidator, {
    ruleText: (arg) => {
        return getNumberRuleText(arg);
    }
});
const IsInteger = createValidator(IsIntegerValidator, {
    ruleText: (arg) => {
        return getIntegerRuleText(arg);
    }
});
const IsGuid = createValidator(() => (val) => {
    if (!val) {
        return [];
    }
    let regex = new RegExp(/^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/);
    const passes = regex.test(val);
    if (!passes) {
        return {
            i18nKey: 'common:textMustBeAGuid',
            defaultValue: 'Must be a guid'
        };
    }
    return [];
});
const IsStringValidator = (config) => (val) => {
    if (!val && val !== '') {
        return [];
    }
    if (typeof val !== 'string') {
        return {
            i18nKey: 'common:textMustBeAString',
            defaultValue: 'Must be a string'
        };
    }
    if (config) {
        if ('minLength' in config) {
            if (val.length < +config.minLength) {
                return {
                    i18nKey: 'common:textMustHaveAtLeastLengthCharacters',
                    defaultValue: 'Must have at least __length__ characters',
                    context: {
                        length: '' + config.minLength
                    }
                };
            }
        }
        if ('maxLength' in config) {
            if (val.length > +config.maxLength) {
                return {
                    i18nKey: 'common:textCanOnlyHaveLengthCharacters',
                    defaultValue: 'Can only have __length__ characters',
                    context: {
                        length: '' + config.maxLength
                    }
                };
            }
        }
    }
    return [];
};
const IsString = createValidator(IsStringValidator);
const IsAny = createValidator(() => () => {
    return [];
});
const IsAlphaNumeric = createValidator(() => (val) => {
    if (val && !(/^(\w|-)*$/).test(val)) {
        return {
            i18nKey: 'common:textMustBeAlphanumeric',
            defaultValue: 'Must be alphanumeric'
        };
    }
    return [];
});
const MustBeTrueOrFalse = (i18nKey, defaultValue, mustBeTrue, attribute) => createValidator(() => (_, extras) => {
    const record = extras.ent;
    const val = record[attribute];
    const passes = mustBeTrue ? val === true : val === false;
    if (!passes) {
        return {
            i18nKey,
            defaultValue
        };
    }
    return [];
}, {
    ruleText: {
        i18nKey,
        defaultValue
    }
});
const MinNumberOfRows = (minRows) => createTopLevelValidator((_) => (records) => {
    if (records.length < minRows) {
        return [{
                i18nKey: 'common:textMustSelectAtLeastOneRecord',
                context: {
                    minNumber: minRows + ''
                },
                defaultValue: 'Must select at least __minNumber__ row(s)'
            }];
    }
    else {
        return [];
    }
});
const MaxNumberOfRows = (maxRows) => createTopLevelValidator((_) => (records) => {
    if (records.length > maxRows) {
        return [{
                i18nKey: 'common:textCannotSelectMoreThanNumberRows',
                context: {
                    maxNumber: maxRows + ''
                },
                defaultValue: 'Cannot select more than__maxNumber__ row(s)'
            }];
    }
    else {
        return [];
    }
});
const AttrMustBeSameAcrossRows = (attrs) => createTopLevelValidator((_) => (records, extras) => {
    const { injector } = extras;
    const i18nService = injector.get(I18nService);
    const failedAttrs = attrs.filter((attribute) => {
        const uniqueValues = uniq(records.map((record) => get(record, attribute.prop)));
        return uniqueValues.length !== 1;
    });
    if ((failedAttrs.length > 0) && (records.length !== 0)) {
        return failedAttrs.map((failedAttr) => {
            return {
                i18nKey: 'common:textNameMustBeUnique',
                context: {
                    name: i18nService.translate(failedAttr.i18nKey, {}, failedAttr.defaultValue)
                },
                defaultValue: '__name__ must be unique'
            };
        });
    }
    else {
        return [];
    }
});
const isBooleanValidation = (val) => {
    if ((val !== '') &&
        (val !== undefined) &&
        (val !== null) &&
        (typeof val !== 'boolean')) {
        return {
            i18nKey: 'common:textMustBeTrueOrFalse',
            defaultValue: 'Must be "true" or "false"',
        };
    }
    return [];
};
const IsBoolean = createValidator(() => (val) => {
    return isBooleanValidation(val);
}, {
    ruleText: {
        i18nKey: 'common:textMustBeTrueOrFalse2',
        defaultValue: 'Must be true or false'
    }
});
const IsBooleanDefaultToFalse = createValidator(() => (val) => {
    return isBooleanValidation(val);
}, {
    ruleText: {
        i18nKey: 'common:textMustBeTrueOrFalseDefaultsToFalse',
        defaultValue: 'Must be true or false. Defaults to false.'
    }
});
const IsDate = createValidator(() => (val) => {
    if (valueIsEmpty(val)) {
        return [];
    }
    if (!(val instanceof Date)) {
        val = new Date(val);
    }
    if (isNaN(val.getTime())) {
        return {
            i18nKey: 'common:textMustBeValidDate',
            defaultValue: 'Must be a valid date'
        };
    }
    return [];
});
const IsOneOfTranslated = createValidator((oneOfDetails) => (_, extras) => {
    const record = extras.ent;
    const i18nService = extras.injector.get(I18nService);
    const val = record[oneOfDetails.statusProp];
    let passes = true;
    let values;
    const isAccepted = !!oneOfDetails.acceptedValues;
    if (!!oneOfDetails.acceptedValues) {
        const requiredVals = oneOfDetails.acceptedValues.map((item) => item.value);
        passes = requiredVals.includes(val.id);
        values = oneOfDetails.acceptedValues.map((item) => {
            return i18nService.translate(item.i18nKey, {}, item.defaultValue);
        }).join(', ');
    }
    else if (!!oneOfDetails.unacceptedValues) {
        const notAcceptedVals = oneOfDetails.unacceptedValues.map((item) => item.value);
        passes = !notAcceptedVals.includes(val.id);
        values = oneOfDetails.unacceptedValues.map((item) => {
            return i18nService.translate(item.i18nKey, {}, item.defaultValue);
        }).join(', ');
    }
    if (!valueIsEmpty(val.id) && !passes) {
        return {
            i18nKey: isAccepted ?
                'common:textMustBeOneOfValues' :
                'common:textCannotBeOneOfValues',
            defaultValue: isAccepted ?
                'Must be one of: __values__' :
                'Cannot be one of: __values__',
            context: {
                values
            }
        };
    }
    return [];
});
const IsOneOf = createValidator((oneOf) => (val) => {
    if (!valueIsEmpty(val) &&
        !oneOf.includes(val)) {
        return {
            i18nKey: 'common:textMustBeOneOfValues',
            defaultValue: 'Must be one of: __values__',
            context: {
                values: oneOf.map(a => a.toString()).join(', ')
            }
        };
    }
    return [];
}, {
    ruleText: {
        i18nKey: 'common:textMustBeOneOfTheAcceptedValues',
        defaultValue: 'Must be one of the accepted values',
    }
});
const IsOneOfMulti = createValidator((oneOf) => (val) => {
    if (!valueIsEmpty(val)) {
        const vals = val.split(',');
        let invalid = false;
        vals.forEach((item) => {
            if (!oneOf.includes(item.trim())) {
                invalid = true;
            }
        });
        if (invalid) {
            return {
                i18nKey: 'common:textMustBeOneOfValues',
                defaultValue: 'Must be one of: __values__',
                context: {
                    values: oneOf.map(a => a.toString()).join(', ')
                }
            };
        }
    }
    return [];
}, {
    ruleText: {
        i18nKey: 'common:textMustBeOneOfTheAcceptedValues',
        defaultValue: 'Must be one of the accepted values'
    }
});
const Unique = createValidator((allowEmpty = false) => (val, { attr, ent, group }) => {
    if (typeof val === 'string') {
        val = trim(val);
    }
    if (allowEmpty && !val) {
        return [];
    }
    if (val === null || val === undefined) {
        return [];
    }
    const failed = group.some((obj) => (obj[attr] === val) && (obj !== ent));
    if (failed) {
        return {
            i18nKey: 'common:textMustBeUnique',
            defaultValue: 'Must be unique'
        };
    }
    return [];
}, {
    ruleText: {
        i18nKey: 'common:textMustBeUnique',
        defaultValue: 'Must be unique'
    }
});
const IsEmail = createValidator(() => (val) => {
    if (valueIsEmpty(val)) {
        return [];
    }
    // trim any trailing spaces prior to validation
    val = val.trim();
    const failed = !RegexUI.emailRegEx.test(val);
    if (failed) {
        return {
            i18nKey: 'common:textMustBeValidEmail',
            defaultValue: 'Must be valid email address'
        };
    }
    return [];
});
const EqualsValueValidator = createValidator((compareConfig) => (val) => {
    if (valueIsEmpty(val)) {
        return [];
    }
    const failed = compareConfig.valToEqual != val;
    if (failed) {
        return {
            i18nKey: compareConfig.i18nKey,
            defaultValue: compareConfig.i18nDefaultValue
        };
    }
    return [];
});
const IsArrayOfType = createValidator((type) => (prop) => {
    if (prop === null || prop === undefined) {
        return [];
    }
    const valid = prop instanceof Array ? prop.every((row) => {
        if (type === 'number' && isNaN(row)) {
            return false;
        }
        if (typeof type === 'string') {
            return typeof row === type;
        }
        return row instanceof type;
    }) : false;
    if (!valid) {
        return {
            i18nKey: 'common:textMustBeListOfTypeX',
            defaultValue: 'Must be a list of type __type__',
            context: {
                type: type instanceof Function ? type.name : type
            }
        };
    }
    return [];
});
/**
 * looks for 'dynamicType' on the external context and expects either 'number' or 'string'
 */
const IsDynamicType = createValidator((config) => (prop, extras) => {
    const { externalContext } = extras;
    const dynamicType = externalContext.dynamicType;
    switch (dynamicType) {
        case 'string':
            return IsStringValidator(config)(prop);
        case 'number':
            return IsNumberValidator(config)(prop);
    }
});
const composeVoid = (fns) => () => (a1, a2) => {
    fns.map(fn => fn(a1, a2));
};
const CSVBooleanFactory = (defaultValue) => composeVoid([
    IsBoolean(),
    Transform((val) => {
        if (typeof val !== 'boolean') {
            const lowerCase = val?.toLowerCase();
            if (lowerCase === 'true') {
                return true;
            }
            else if (lowerCase === 'false') {
                return false;
            }
            else if (defaultValue === undefined) {
                return val;
            }
            else if (!val) {
                return defaultValue;
            }
        }
        return val;
    })
]);
const CSVBooleanDefaultToFalse = composeVoid([
    IsBooleanDefaultToFalse(),
    Transform((val) => {
        if (typeof val !== 'boolean') {
            const lowerCase = val?.toLowerCase();
            if (lowerCase === 'true') {
                return true;
            }
            else if (lowerCase === 'false') {
                return false;
            }
            else {
                return false;
            }
        }
        return val;
    })
]);
const CSVBoolean = CSVBooleanFactory();
const IsNumberTransformedFactory = () => composeVoid([
    IsNumber(),
    Transform((val) => {
        return val ? Number(val) : val;
    })
]);
const IsNumberTransformed = IsNumberTransformedFactory();
const IsStringTransformedFactory = (config) => composeVoid([
    IsString(config),
    Transform((val) => {
        return val ? '' + val : '';
    })
]);
const IsStringTransformed = IsStringTransformedFactory();
const CSVDateFactory = () => composeVoid([
    IsDate(),
    Transform((val) => {
        if (val) {
            return new Date(val);
        }
        return '';
    })
]);
const CSVDate = CSVDateFactory();
// helpers
function valueIsEmpty(value) {
    return value === null || value === undefined || value === '';
}
const classDescriptionStorage = new WeakMap();
/**
 *
 * @param description This is a decorator that saves a property definition to show in import rules table (e.g. 'createdDate' might have the description 'Date the item was created')
 * @returns N/A
 */
function Description(description) {
    return (arg, prop) => {
        const existing = classDescriptionStorage.get(arg.constructor) ?? {};
        existing[prop] = description;
        classDescriptionStorage.set(arg.constructor, existing);
    };
}
function getImportClassDescriptions(importClass) {
    const classDescription = classDescriptionStorage.get(importClass);
    return classDescription;
}
/**
 *
 * @param validationClass This is the class that the validation of the file is based on
 * @param externalContext This is used here to get dynamic rule text (e.g. @IsNumber({min: 1}))
 * @returns A list of table rows for the import rules table, includes the column name, list of rules, and description of the column
 */
function getImportRulesTableRows(validationClass, externalContext) {
    const validatorMap = getValidatorMap(validationClass);
    const descriptionsMap = getImportClassDescriptions(validationClass);
    const validatorsPerColumn = Object.keys(validatorMap)
        .map((key) => key)
        .reduce((acc, curr) => {
        // throw error if no description
        if (!descriptionsMap[curr]) {
            throw new Error(`No description provided for ${curr}`);
        }
        return [
            ...acc,
            {
                column: curr,
                validators: validatorMap[curr],
                rules: getAllRules(validatorMap[curr], curr, externalContext),
                definition: descriptionsMap[curr]
            }
        ];
    }, []);
    return validatorsPerColumn;
}
/**
 *
 * @param validators This is a collection of the validation classes
 * @param key This is the attr that is being evaluated
 * @param externalContext This is used here to get dynamic rule text (e.g. @IsNumber({min: 1}))
 * @returns A list of import rules based on validators applied to an import column
 */
function getAllRules(validators, key, externalContext) {
    const allRulesText = validators.reduce((acc, curr) => {
        if (!(curr.extras && curr.extras.ruleText)) {
            return acc;
        }
        // ruleText might be a lang object OR a function that returns the lang object
        return [
            ...acc,
            curr.extras.ruleText instanceof Function ?
                curr.extras.ruleText(curr.arg, {
                    externalContext,
                    attr: key
                }) : curr.extras.ruleText
        ];
    }, []);
    return allRulesText.filter((a) => !!a).reverse();
}
function ServiceValidator(Service, method, ruleInfo) {
    return createValidator(() => (value, extras) => {
        const service = extras.injector.get(Service);
        return service[method](value, extras);
    }, !!ruleInfo ? { ruleText: ruleInfo } : undefined);
}

/**
 * Generated bundle index. Do not edit.
 */

export { AttrMustBeSameAcrossRows, CSVBoolean, CSVBooleanDefaultToFalse, CSVBooleanFactory, CSVDate, CSVDateFactory, CSV_COLUMN_REGEX, CsvColumnValidator, Description, DuplicateCheckValidator, EVALUATE_CHANGE, EmailExtensionValidator, EmailValidator, EqualsValueValidator, ExactArrayLengthValidator, FIRST_CHARACTER_REGEX, FormControlValidationModule, InputRegexService, IsAlphaNumeric, IsAny, IsArrayOfType, IsBoolean, IsBooleanDefaultToFalse, IsDate, IsDynamicType, IsEmail, IsGuid, IsInteger, IsNumber, IsNumberTransformed, IsNumberTransformedFactory, IsOneOf, IsOneOfMulti, IsOneOfTranslated, IsString, IsStringTransformed, IsStringTransformedFactory, MaskValidator, MaxNumberOfRows, MinMaxArrayValidator, MinMaxValidator, MinMaxWordsValidator, MinNumberOfRows, MustBeTrueOrFalse, Optional, PatternValidator, REFLECTED_DATA_REGEX, ReflectedDataValidator, RegexUI, RegexValidator, Required, RequiredArrayValidator, RequiredCheckboxValidator, RequiredCustomValidator, RequiredFormCheckbox, RequiredIfOtherHasValue, ServiceValidator, SiblingValueValidator, TEXT_FRIENDLY_REGEX, TextFriendlySpecialCharCleaner, Transform, URLValidator, URL_REGEX, Unique, applyTransformations, composeVoid, confirmationValidator, createTopLevelValidator, createValidator, getAllRules, getImportClassDescriptions, getImportRulesTableRows, getIntegerRuleText, getKeys, getNumberRuleText, getOrganizedErrors, getValidatorMap, handleMinMaxValidation, transformStorage, validateMultiple };
